import { Database, Gauge, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { STORAGE_TANKS, TANK_HEALTH } from './mockAssetData';

function HealthBadge({ health }) {
  const config = TANK_HEALTH[health];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function InventoryGauge({ inventory, capacityNum, health }) {
  const pct = Math.round((inventory / capacityNum) * 100);
  const barClass = TANK_HEALTH[health].barClass;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-[9px]">
        <span className="inline-flex items-center gap-1 text-logi-text0">
          <Gauge className="h-3 w-3" />
          Live Inventory
        </span>
        <span className="font-mono font-bold text-white">
          {inventory.toLocaleString()} MT · {pct}%
        </span>
      </div>
      <div className="mt-0.5 h-2 overflow-hidden rounded-full bg-logi-silver/80">
        <div
          className={`h-full rounded-full transition-all ${barClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function StorageTanksGrid({ tanks = STORAGE_TANKS }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return tanks.filter(
      (t) =>
        t.id.toLowerCase().includes(q) ||
        t.product.toLowerCase().includes(q) ||
        TANK_HEALTH[t.health].label.toLowerCase().includes(q),
    );
  }, [tanks, query]);

  const criticalCount = tanks.filter((t) => t.health === 'critical' || t.health === 'maintenance').length;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Storage Tanks · Depot &amp; Refinery</h3>
            <p className="text-[11px] text-logi-text0">
              Fixed-site bulk storage · Live inventory levels · Tank health monitoring
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {criticalCount > 0 && (
            <span className="rounded-full bg-red-50 px-2.5 py-1 text-[10px] font-bold text-red-700 ring-1 ring-red-500/25">
              {criticalCount} require attention
            </span>
          )}
          <div className="relative w-full sm:w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-logi-text0" />
            <input
              type="search"
              placeholder="Search depot tanks…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-lg border border-logi-grid bg-white py-1.5 pl-8 pr-3 text-xs text-logi-text placeholder-logi-subtle outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-logi-accent/30"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6">
        {filtered.map((tank) => (
          <article
            key={tank.id}
            className={`rounded-lg border bg-white p-2.5 backdrop-blur-sm transition-colors hover:bg-logi-silver/80 ${
              tank.health === 'critical'
                ? 'border-red-200 ring-1 ring-red-500/10'
                : tank.health === 'maintenance'
                  ? 'border-violet-500/30'
                  : 'border-logi-grid'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-mono text-xs font-bold text-logi-accent">{tank.id}</span>
              <HealthBadge health={tank.health} />
            </div>

            <div className="mt-2 space-y-1">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-logi-text0">Total Capacity</span>
                <span className="font-mono font-semibold text-logi-text">{tank.capacity}</span>
              </div>
              <div className="flex items-start justify-between gap-1 text-[10px]">
                <span className="text-logi-text0">Product Allocation</span>
                <span className="max-w-[60%] truncate text-right font-medium text-logi-muted">
                  {tank.product}
                </span>
              </div>
            </div>

            <InventoryGauge
              inventory={tank.inventory}
              capacityNum={tank.capacityNum}
              health={tank.health}
            />
          </article>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="py-8 text-center text-sm text-logi-text0">No storage tanks match your search.</p>
      )}
    </section>
  );
}
